import { provideHttpClient, withInterceptors } from '@angular/common/http'
import {
  APP_INITIALIZER,
  EnvironmentProviders,
  ErrorHandler,
  importProvidersFrom,
  LOCALE_ID,
  makeEnvironmentProviders,
  provideZoneChangeDetection,
} from '@angular/core'
import { provideAnimations } from '@angular/platform-browser/animations'
import {
  provideRouter,
  Routes,
  withComponentInputBinding,
} from '@angular/router'
import { provideIonicAngular } from '@ionic/angular/standalone'
import { State, Store } from '@ngrx/store'
import { provideStoreDevtools } from '@ngrx/store-devtools'
import {
  RECAPTCHA_LOADER_OPTIONS,
  RECAPTCHA_V3_SITE_KEY,
  RecaptchaV3Module,
} from 'ng-recaptcha-2'
import { provideNgxMask } from 'ngx-mask'
import { LIB_INTERCEPTORS } from '../http/lib-interceptors'
import { setAppId } from '../modules/config/config.actions'
import { ConfigService } from '../modules/config/config.service'
import { AdobeTargetService } from '../modules/tagging/adobe/adobe-target.service'
import { ABTestKey } from '../modules/ui'
import { provideDrrStore } from '../store/provide-drr-store'
import { provideRouterStoreConfig } from '../store/provide-router-store'
import { provideI18n } from './i18n/i18n.module'
import { I18nTranslations } from './i18n/i18n.types'
import { registerIonicons } from './icons'
import { DRR_BASE_HREF, SHARED_CONFIG, SharedConfig } from './shared.config'
import { extractRapAppIdFromPath, getRapAppId } from './utils/branding'

export class DefaultErrorHandler implements ErrorHandler {
  handleError(err: any): void {
    console.error(`Uncaught error: ${err}`)
  }
}

const shouldGetPropositions = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search)
  const getPropositionsParam = urlParams.get('getpropositions')?.toLowerCase()
  return getPropositionsParam !== 'off'
}

export function provideShared(config: SharedConfig): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: SHARED_CONFIG, useValue: config },
    { provide: DRR_BASE_HREF, useValue: config.drrBaseHref },
    provideNgxMask(),
    importProvidersFrom(RecaptchaV3Module),
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useFactory: (configService: ConfigService) => {
        return configService.getConfig().googleCaptchaV3Key
      },
      deps: [ConfigService],
    },
    {
      provide: RECAPTCHA_LOADER_OPTIONS,
      useFactory: () => ({
        onBeforeLoad(url) {
          url.searchParams.set('hl', localStorage.getItem('locale'))
          return { url }
        },
      }),
      deps: [LOCALE_ID],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (adobeTargetService: AdobeTargetService) => () => {
        if (!shouldGetPropositions()) {
          return Promise.resolve()
        }
        return adobeTargetService.getTargetPropositions([
          { key: ABTestKey.LANDMARK_ZOOM },
        ])
      },
      deps: [AdobeTargetService],
      multi: true,
    },
  ])
}

export function provideDrrApp({
  routes,
  environment,
  i18nTranslations,
}: {
  routes: Routes
  environment: SharedConfig
  i18nTranslations?: I18nTranslations
}): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideZoneChangeDetection(),
    provideRouter(routes, withComponentInputBinding()),
    provideAnimations(),
    provideHttpClient(withInterceptors(LIB_INTERCEPTORS)),
    provideIonicAngular(),
    provideDrrStore(),
    provideRouterStoreConfig(),
    provideStoreDevtools({
      maxAge: 30,
      logOnly: environment.log,
      connectInZone: true,
    }),
    provideShared({
      drrBaseHref: '/',
      additionalCookieDomain: environment.additionalCookieDomain,
      association: environment.association,
      baseURL: environment.baseURL,
      clientId: environment.clientId,
      configTools: environment.configTools,
      description: environment.description,
      displayVersion: environment.displayVersion,
      googleCaptchaKey: environment.googleCaptchaKey,
      googleCaptchaV3Key: environment.googleCaptchaV3Key,
      thirdPartyCookiesUrl: environment.thirdPartyCookiesUrl,
      unsecureClub: environment.unsecureClub,
      nativeAppView: environment.nativeAppView,
      homeBackButtonRedirectTo: environment.homeBackButtonRedirectTo,
      optimize: environment.optimize,
      log: environment.log,
      openAARsURL: environment.openAARsURL,
      ipInterrogateURL: environment.ipInterrogateURL,
      hotjar: environment.hotjar,
      awsLocationMapsURL: environment.awsLocationMapsURL,
      awsLocationApiKey: environment.awsLocationApiKey,
      maplibreWorkerPath: environment.maplibreWorkerPath,
    }),
    provideI18n(i18nTranslations),
    { provide: ErrorHandler, useClass: DefaultErrorHandler },
    { provide: State, useValue: null },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        registerIonicons()
        return () => Promise.resolve()
      },
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (store: Store) => () => {
        const appId = extractRapAppIdFromPath(window.location.pathname) ?? getRapAppId()
        if (appId) {
          store.dispatch(setAppId({ payload: appId }))
        }
        return Promise.resolve()
      },
      deps: [Store],
      multi: true,
    },
  ])
}
